import { useState, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { Project } from '../../api/types'
import type { PayApp } from '../../api/types'
import { createRetainageRelease } from '../../api/projects'
import { payAppEditorStep } from '../../lib/payAppEditorSteps'
import { payAppCtaModel } from '../../lib/payAppCta.js'

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
)

type Props = {
  projectId: number
  project?: Project | null
  payApps: PayApp[]
  testId?: string
  className?: string
}

export default function PayAppCta({
  projectId,
  project,
  payApps,
  testId = 'pay-app-cta',
  className = '',
}: Props) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const retentionHeld = parseFloat(project?.retention_held ?? '0') || 0
  const hasRetainageRelease = payApps.some((pa) => pa.is_retainage_release)
  const model = payAppCtaModel({ project, payApps, retentionHeld, hasRetainageRelease })

  if (model.variant === 'hidden') return null

  async function handleClick(e: MouseEvent) {
    e.stopPropagation()
    if (busy) return

    if (model.variant === 'draft' && model.draftPayAppId != null) {
      const draft = payApps.find((pa) => pa.id === model.draftPayAppId)
      const step = draft ? payAppEditorStep(draft) : 1
      navigate(`/projects/${projectId}/pay-apps/${model.draftPayAppId}?step=${step}`)
      return
    }

    if (model.variant === 'retention') {
      setBusy(true)
      try {
        const result = await createRetainageRelease(projectId)
        await qc.invalidateQueries({ queryKey: ['project-payapps', projectId] })
        if (result.pay_app_id) {
          navigate(`/projects/${projectId}/pay-apps/${result.pay_app_id}?step=1`)
        }
      } finally {
        setBusy(false)
      }
      return
    }

    navigate(`/projects/${projectId}/pay-apps/new`)
  }

  return (
    <div className={`pay-app-cta-wrap${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className="pay-app-cta"
        data-testid={testId}
        data-variant={model.variant}
        aria-label={model.ariaLabel}
        disabled={busy}
        onClick={handleClick}
      >
        <PlusIcon />
        <span className="pay-app-cta-label">{busy ? '…' : model.primary}</span>
      </button>
      <div className="pay-app-cta-caption">{model.secondary}</div>
    </div>
  )
}
